/**
 * GPU Pool Readiness — checks every online comfyui-img host (through the gateway)
 * for the nodes + models the studio production fragments need, and flags what's
 * missing per host and per fragment. Backed by GET /admin/gpu/readiness.
 */
import { useQuery } from '@tanstack/react-query';
import { RefreshCw, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { fetchGpuReadiness, type HostReadiness } from '@/services/api/gpu-readiness';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';

function MissingList({ items, label }: { items: string[]; label: string }) {
  if (!items.length) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((i) => (
        <Badge key={i} variant="outline" className="border-red-400/40 bg-red-400/10 font-mono text-[10px] text-red-500" title={`missing ${label}`}>
          {i}
        </Badge>
      ))}
    </div>
  );
}

function hostOk(h: HostReadiness): boolean {
  return h.reachable && h.missingNodes.length === 0 && h.missingModels.length === 0;
}

export default function GpuReadinessPage() {
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['gpu-readiness'],
    queryFn: fetchGpuReadiness,
    staleTime: 30_000,
  });

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">GPU pool readiness</h1>
          <p className="text-sm text-muted-foreground">
            Every online <span className="font-mono">comfyui-img</span> host, checked through the gateway for the production fragments&rsquo; nodes + models.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`mr-2 size-4 ${isFetching ? 'animate-spin' : ''}`} /> Re-check
        </Button>
      </div>

      {isLoading && <Skeleton className="h-40 w-full" />}
      {error && (
        <Card>
          <CardContent className="flex items-center gap-2 p-4 text-sm text-red-500">
            <AlertTriangle className="size-4" /> Couldn&rsquo;t load readiness: {(error as Error).message}
          </CardContent>
        </Card>
      )}

      {data && (
        <>
          {/* summary */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Card><CardContent className="p-4">
              <div className="text-xs text-muted-foreground">Status</div>
              <div className="mt-1 flex items-center gap-1.5 text-lg font-semibold">
                {data.allReady
                  ? <><CheckCircle2 className="size-5 text-emerald-500" /> Ready</>
                  : <><XCircle className="size-5 text-red-500" /> Gaps</>}
              </div>
            </CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Hosts</div><div className="mt-1 text-2xl font-semibold">{data.hosts.length}</div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Required nodes</div><div className="mt-1 text-2xl font-semibold">{data.requiredNodes}</div></CardContent></Card>
            <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Required models</div><div className="mt-1 text-2xl font-semibold">{data.requiredModels}</div></CardContent></Card>
          </div>

          {data.gatewayError && (
            <Card><CardContent className="flex items-center gap-2 p-3 text-sm text-amber-500">
              <AlertTriangle className="size-4" /> gateway note: {data.gatewayError}
            </CardContent></Card>
          )}

          {/* fragments */}
          <div>
            <h2 className="mb-2 text-sm font-semibold">Fragments <span className="text-muted-foreground">· ready when present on every host</span></h2>
            <div className="flex flex-wrap gap-2">
              {data.fragments.map((f) => (
                <Badge key={f.template} variant="outline"
                  className={f.ready ? 'border-emerald-400/40 bg-emerald-400/10 text-emerald-600' : 'border-red-400/40 bg-red-400/10 text-red-500'}
                  title={f.ready ? 'all deps present' : `missing: ${[...f.missingNodes, ...f.missingModels].join(', ')}`}>
                  {f.ready ? <CheckCircle2 className="mr-1 size-3" /> : <XCircle className="mr-1 size-3" />}
                  {f.template}
                </Badge>
              ))}
            </div>
          </div>

          {/* hosts */}
          <div>
            <h2 className="mb-2 text-sm font-semibold">Hosts</h2>
            <Card><CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Host</TableHead>
                    <TableHead>Datacenter</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Missing nodes</TableHead>
                    <TableHead>Missing models</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.hosts.map((h) => (
                    <TableRow key={h.label}>
                      <TableCell className="font-mono text-xs">{h.label}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{h.datacenter ?? '—'}</TableCell>
                      <TableCell>
                        {!h.reachable
                          ? <Badge variant="outline" className="border-red-400/40 bg-red-400/10 text-red-500" title={h.error}>unreachable</Badge>
                          : hostOk(h)
                            ? <Badge variant="outline" className="border-emerald-400/40 bg-emerald-400/10 text-emerald-600">ready</Badge>
                            : <Badge variant="outline" className="border-amber-400/40 bg-amber-400/10 text-amber-600">missing</Badge>}
                      </TableCell>
                      <TableCell><MissingList items={h.missingNodes} label="node" /></TableCell>
                      <TableCell><MissingList items={h.missingModels} label="model" /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent></Card>
          </div>

          <p className="text-xs text-muted-foreground">Checked {new Date(data.checkedAt).toLocaleString()}.</p>
        </>
      )}
    </div>
  );
}
